(() => {
  const root = window.NailvoTheme?.root || '/';
  const cartAddUrl = window.NailvoTheme?.cartAddUrl || `${root}cart/add`;
  const moneyFormat = window.NailvoTheme?.moneyFormat || '${{amount}}';

  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));

  const escapeHtml = (value = '') =>
    String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  const formatMoney = (cents) => {
    const value = Number(cents || 0) / 100;
    const formatted = value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return moneyFormat
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(value).toLocaleString())
      .replace(/\{\{\s*amount\s*\}\}/, formatted)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, formatted.replace(',', 'X').replace('.', ',').replace('X', '.'));
  };

  const setBodyLock = () => {
    const anyOpen = qs('.cart-drawer.is-open, .mobile-menu.is-open');
    document.body.classList.toggle('is-locked', Boolean(anyOpen));
  };

  const showToast = (message) => {
    const toast = qs('[data-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(showToast.timer);
    showToast.timer = window.setTimeout(() => toast.classList.remove('is-visible'), 2600);
  };

  const initReveal = () => {
    const items = qsa('[data-reveal]');
    if (!items.length) return;
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    items.forEach((item) => observer.observe(item));
  };

  const initMenu = () => {
    const menu = qs('[data-mobile-menu]');
    const openButton = qs('[data-menu-open]');
    if (!menu || !openButton) return;

    const open = () => {
      menu.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      openButton.setAttribute('aria-expanded', 'true');
      setBodyLock();
      qs('[data-menu-close]', menu)?.focus();
    };

    const close = () => {
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      openButton.setAttribute('aria-expanded', 'false');
      setBodyLock();
      openButton.focus();
    };

    openButton.addEventListener('click', open);
    qsa('[data-menu-close]', menu).forEach((button) => button.addEventListener('click', close));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && menu.classList.contains('is-open')) close();
    });
  };

  const drawer = qs('[data-cart-drawer]');

  const openCart = () => {
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    setBodyLock();
    qs('[data-cart-close]', drawer)?.focus();
  };

  const closeCart = () => {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    setBodyLock();
  };

  const renderCart = (cart) => {
    qsa('[data-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
    });

    if (!drawer) return;
    const itemsContainer = qs('[data-cart-items]', drawer);
    const footer = qs('[data-cart-footer]', drawer);
    const total = qs('[data-cart-total]', drawer);
    if (!itemsContainer || !footer) return;

    if (!cart.items.length) {
      itemsContainer.innerHTML = `
        <div class="cart-empty">
          <p>Your cart is empty.</p>
          <a class="text-link" href="${root}collections/all">Find your Nailvo</a>
        </div>`;
      footer.classList.add('is-hidden');
      return;
    }

    itemsContainer.innerHTML = cart.items
      .map((item) => {
        const image = item.image
          ? `<img src="${escapeHtml(item.image)}&width=180" alt="${escapeHtml(item.product_title)}" width="90" height="90" loading="lazy">`
          : '';
        const variant = item.variant_title && item.variant_title !== 'Default Title'
          ? `<small>${escapeHtml(item.variant_title)}</small>`
          : '';
        return `
          <article class="cart-item" data-key="${escapeHtml(item.key)}">
            ${image}
            <div class="cart-item__content">
              <a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
              ${variant}
              <div class="cart-item__meta">
                <span>Qty ${item.quantity}</span>
                <span>${formatMoney(item.final_line_price)}</span>
              </div>
            </div>
          </article>`;
      })
      .join('');

    if (total) total.textContent = formatMoney(cart.total_price);
    footer.classList.remove('is-hidden');
  };

  const refreshCart = async () => {
    const response = await fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Cart could not be refreshed');
    const cart = await response.json();
    renderCart(cart);
    return cart;
  };

  const initCart = () => {
    qsa('[data-cart-open]').forEach((button) => button.addEventListener('click', openCart));
    qsa('[data-cart-close]').forEach((button) => button.addEventListener('click', closeCart));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && drawer?.classList.contains('is-open')) closeCart();
    });
  };

  const initQuantity = () => {
    qsa('.quantity-control').forEach((control) => {
      const input = qs('input[type="number"]', control);
      if (!input) return;
      qs('[data-qty-minus]', control)?.addEventListener('click', () => {
        input.value = Math.max(Number(input.min || 1), Number(input.value || 1) - 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      qs('[data-qty-plus]', control)?.addEventListener('click', () => {
        input.value = Number(input.value || 1) + 1;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  };

  const initProductForms = () => {
    qsa('[data-product-form]').forEach((form) => {
      const submit = qs('button[type="submit"]', form);
      const label = qs('[data-add-label]', form);

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (!submit || submit.disabled) return;
        submit.disabled = true;
        submit.classList.add('is-loading');
        const original = label?.textContent;
        if (label) label.textContent = 'Adding…';

        try {
          const response = await fetch(`${cartAddUrl}.js`, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: new FormData(form)
          });
          const result = await response.json();
          if (!response.ok) throw new Error(result.description || 'This item could not be added.');
          await refreshCart();
          if (label) label.textContent = 'Added';
          openCart();
          window.setTimeout(() => {
            if (label) label.textContent = original || 'Add to cart';
          }, 1400);
        } catch (error) {
          showToast(error.message || 'Something went wrong. Please try again.');
          if (label) label.textContent = original || 'Add to cart';
        } finally {
          submit.disabled = false;
          submit.classList.remove('is-loading');
        }
      });
    });
  };

  const initProductVariants = () => {
    qsa('[data-product-section]').forEach((section) => {
      const form = qs('[data-product-form]', section);
      const select = qs('[data-variant-select]', section);
      if (!form || !select) return;
      const optionGroups = qsa('.product-option', form);

      const updateVariant = () => {
        const selected = optionGroups.map((group) => qs('input:checked', group)?.value);
        const options = qsa('option', select);
        const match = options.find((option) => {
          try {
            return JSON.parse(option.dataset.options || '[]').every((value, index) => value === selected[index]);
          } catch (_) {
            return false;
          }
        });
        if (!match) return;
        select.value = match.value;
        const submit = qs('button[type="submit"]', form);
        const label = qs('[data-add-label]', form);
        if (submit) submit.disabled = match.disabled;
        if (label) label.textContent = match.disabled ? 'Sold out' : 'Add to cart';
      };

      optionGroups.forEach((group) => {
        qsa('input[type="radio"]', group).forEach((radio) => radio.addEventListener('change', updateVariant));
      });
    });
  };

  const initGallery = () => {
    qsa('[data-product-section]').forEach((section) => {
      const main = qs('[data-product-main-media] img', section);
      const buttons = qsa('[data-media-src]', section);
      if (!main || !buttons.length) return;
      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          main.src = button.dataset.mediaSrc;
          main.srcset = '';
          main.alt = button.dataset.mediaAlt || main.alt;
          buttons.forEach((item) => item.classList.remove('is-active'));
          button.classList.add('is-active');
        });
      });
    });
  };

  const initMobileBuybar = () => {
    qsa('[data-scroll-to-product]').forEach((button) => {
      button.addEventListener('click', () => {
        const form = qs('.product-form--page');
        form?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        window.setTimeout(() => qs('button[type="submit"]', form)?.focus(), 500);
      });
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    initReveal();
    initMenu();
    initCart();
    initQuantity();
    initProductForms();
    initProductVariants();
    initGallery();
    initMobileBuybar();
  });
})();
